// Radha — trend & DSO, surfaced on Overview. Snapshots accumulate daily via
// pg_cron (055); "Snapshot now" exists so the trend has something to show
// before a week of history has actually built up, and to refresh today's
// figure on demand.
import React from "react";

import { useSnapshots, useTakeSnapshot } from "../hooks/useReporting";
import { Sparkline } from "./Sparkline";
import { fmtMoney as inr } from "../lib/money";


// General published reference ranges for context, not a live benchmark feed —
// no free API exists for real-time industry DSO data; stated plainly here.
const DSO_REFERENCE = "Typical B2B services DSO runs ~45–65 days; 90+ signals a collections gap worth investigating.";

export function ReceivablesTrend({ canWrite }: { canWrite: boolean }) {
  const { data: snapshots, isLoading } = useSnapshots();
  const snap = useTakeSnapshot();

  const latest = snapshots?.[snapshots.length - 1];
  const totalSeries = (snapshots ?? []).map((s) => Number(s.total_outstanding));
  const overdueSeries = (snapshots ?? []).map((s) => Number(s.overdue_amount));

  if (isLoading) return null;

  return (
    <div className="sanctuary-card sanctuary-reveal" style={{ padding: "16px 18px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={{ font: "600 15px Inter" }}>Trend &amp; DSO</div>
        {canWrite && (
          <button onClick={() => snap.mutate()} disabled={snap.isPending}
            style={{ padding: "3px 10px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
            {snap.isPending ? "…" : "Snapshot now"}
          </button>
        )}
      </div>

      {!latest ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No snapshots yet — take one to start tracking trend.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 18 }}>
          <div>
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Outstanding</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <div style={{ font: "600 18px Inter" }}>{inr(Number(latest.total_outstanding))}</div>
              {totalSeries.length >= 2 && <Sparkline values={totalSeries} width={72} height={22} />}
            </div>
          </div>
          <div>
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Overdue</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <div style={{ font: "600 18px Inter", color: "var(--status-critical)" }}>{inr(Number(latest.overdue_amount))}</div>
              {overdueSeries.length >= 2 && <Sparkline values={overdueSeries} width={72} height={22} accent="var(--status-critical)" />}
            </div>
          </div>
          <div>
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }} title={DSO_REFERENCE}>DSO</div>
            <div style={{ font: "600 18px Inter" }}>
              {latest.dso_days ? `${Math.round(Number(latest.dso_days))}d` : <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>n/a</span>}
            </div>
          </div>
          <div>
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Avg days past due</div>
            <div style={{ font: "600 18px Inter" }}>{Math.round(Number(latest.avg_dpd))}d</div>
          </div>
        </div>
      )}
      <p style={{ fontSize: 11, color: "var(--fg-faint)", margin: "10px 0 0" }}>{DSO_REFERENCE}</p>
    </div>
  );
}
